import React, { useState } from 'react';
import { Star, ChevronLeft, ChevronRight } from 'lucide-react';
import { imageData, getAltText } from '../../data/images';

const TestimonialsSection = ({ language, currentContent }) => {
  const { testimonialsImage, testimonialsViewsGallery } = imageData;
  const [currentViewIndex, setCurrentViewIndex] = useState(0);

  const nextView = () => {
    setCurrentViewIndex((prev) => 
      prev === testimonialsViewsGallery.length - 1 ? 0 : prev + 1
    );
  };

  const prevView = () => {
    setCurrentViewIndex((prev) => 
      prev === 0 ? testimonialsViewsGallery.length - 1 : prev - 1
    );
  };

  const goToView = (index) => {
    setCurrentViewIndex(index);
  };

  return (
    <section className="py-20 bg-white">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="text-center mb-16">
          <h2 className="text-4xl md:text-5xl font-bold text-gray-900 mb-4 font-dancing">
            {currentContent.testimonials.title}
          </h2>
          <p className="text-xl text-tilcara-orange font-medium mb-4">
            {currentContent.testimonials.subtitle}
          </p>
          <p className="text-lg text-gray-600 max-w-3xl mx-auto leading-relaxed">
            {currentContent.testimonials.humanSubtitle}
          </p>
        </div>

        <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-8 mb-16">
          {currentContent.testimonials.reviews.map((review, index) => (
            <div key={index} className="bg-gray-50 rounded-xl p-6 shadow-lg hover:shadow-xl transition-shadow duration-300">
              <div className="flex items-center mb-4">
                {[...Array(5)].map((_, i) => (
                  <Star key={i} className="h-5 w-5 text-yellow-400 fill-current" />
                ))}
              </div>
              <p className="text-gray-700 mb-4 italic">
                "{review.text}"
              </p>
              <div className="flex items-center space-x-3">
                <div className="w-10 h-10 bg-tilcara-orange rounded-full flex items-center justify-center text-white font-semibold">
                  {review.author.charAt(0)}
                </div>
                <div>
                  <p className="font-semibold text-gray-900">{review.author}</p>
                  <p className="text-sm text-gray-600">{review.location}</p>
                </div>
              </div>
            </div>
          ))}
        </div>

        {/* Vista Principal Destacada */}
        <div className="text-center mb-12">
          <div className="max-w-5xl mx-auto">
            <img
              src={testimonialsImage.src}
              alt={getAltText(testimonialsImage, language)}
              className="w-full h-[500px] object-cover rounded-2xl shadow-2xl"
            />
            <p className="mt-6 text-2xl text-gray-600 font-dancing">
              {currentContent.testimonials.imageCaption}
            </p>
          </div>
        </div>

        {/* Galería de Vistas Adicionales */}
        <div className="max-w-6xl mx-auto">
          <div className="text-center mb-8">
            <h3 className="text-2xl font-semibold text-gray-900 mb-4">
              {language === 'es' ? 'Más vistas que te esperan' : 'More views awaiting you'}
            </h3>
            <p className="text-lg text-gray-600 max-w-2xl mx-auto">
              {language === 'es' 
                ? 'Todas estas vistas son fotos tomadas 100% desde nuestro hostel. Esto es lo que realmente vas a ver cuando te quedes con nosotros ❤️'
                : 'All these views are photos taken 100% from our hostel. This is what you\'ll actually see when you stay with us ❤️'}
            </p>
          </div>
          
          {/* Slider Principal */}
          <div className="relative mb-8">
            <div className="relative h-[400px] rounded-xl overflow-hidden shadow-lg">
              <img
                src={testimonialsViewsGallery[currentViewIndex].src}
                alt={getAltText(testimonialsViewsGallery[currentViewIndex], language)}
                className="w-full h-full object-cover transition-transform duration-500 hover:scale-105"
              />
              <div className="absolute inset-0 bg-gradient-to-t from-black/30 to-transparent"></div>
              
              {/* Controles del Slider */}
              <button
                onClick={prevView}
                className="absolute left-4 top-1/2 transform -translate-y-1/2 bg-white/80 hover:bg-white text-gray-800 p-2 rounded-full transition-all duration-200 hover:scale-110"
              >
                <ChevronLeft className="h-5 w-5" />
              </button>
              <button
                onClick={nextView}
                className="absolute right-4 top-1/2 transform -translate-y-1/2 bg-white/80 hover:bg-white text-gray-800 p-2 rounded-full transition-all duration-200 hover:scale-110"
              >
                <ChevronRight className="h-5 w-5" />
              </button>
            </div>
          </div>

          {/* Miniaturas */}
          <div className="grid grid-cols-4 md:grid-cols-7 gap-3">
            {testimonialsViewsGallery.map((view, index) => (
              <button
                key={index}
                onClick={() => goToView(index)}
                className={`relative h-20 rounded-lg overflow-hidden transition-all duration-200 ${
                  index === currentViewIndex 
                    ? 'ring-3 ring-tilcara-orange scale-105' 
                    : 'hover:scale-105 opacity-70 hover:opacity-100'
                }`}
              >
                <img
                  src={view.src}
                  alt={getAltText(view, language)}
                  className="w-full h-full object-cover"
                />
              </button>
            ))}
          </div>
          
          {/* Indicadores de navegación */}
          <div className="flex justify-center mt-6 space-x-2">
            {testimonialsViewsGallery.map((_, index) => (
              <button
                key={index}
                onClick={() => goToView(index)}
                className={`w-3 h-3 rounded-full transition-all duration-200 ${
                  index === currentViewIndex 
                    ? 'bg-tilcara-orange' 
                    : 'bg-gray-300 hover:bg-gray-400'
                }`}
              />
            ))}
          </div>
        </div>
      </div>
    </section>
  );
};

export default TestimonialsSection;
